import { ticketService, type Ticket, type TicketStatusValue } from '@/services/ticket-service';
import { navigate } from '@/utils/navigate';
import './nu-user-menu';

function thirtyDaysAgo(): Date {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d;
}

const STATUS_LABELS: Record<TicketStatusValue, string> = {
  'CHECKED-IN':   'Checked In',
  'CHECKED-OUT':  'Checked Out',
  'DONE':         'Done',
  'NOTIFY_ERROR': 'Notify Error',
};

const STATUS_CLASSES: Record<TicketStatusValue, string> = {
  'CHECKED-IN':   'badge-in',
  'CHECKED-OUT':  'badge-out',
  'DONE':         'badge-done',
  'NOTIFY_ERROR': 'badge-err',
};

class NuTickets extends HTMLElement {
  private readonly root: ShadowRoot;
  private _tickets: Ticket[] = [];
  private _loading = false;
  private _error = '';

  private _search = '';
  private _showAll = false;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['org-id']; }
  get orgId() { return this.getAttribute('org-id') ?? ''; }

  connectedCallback() {
    this._load();
  }

  private async _load() {
    this._loading = true;
    this._error = '';
    this._render();

    try {
      const params: Parameters<typeof ticketService.getTickets>[1] = {};
      if (this._search.trim()) {
        params.ticketNum = this._search.trim();
      } else if (this._showAll) {
        params.activeOnly = false;
      } else {
        params.activeOnly = true;
        params.from = thirtyDaysAgo().toISOString();
      }
      this._tickets = await ticketService.getTickets(this.orgId, params);
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Failed to load tickets.';
    }

    this._loading = false;
    this._render();
  }

  private _rowsHTML(): string {
    if (!this._tickets.length) {
      return `<tr><td class="empty-cell" colspan="4">No tickets found.</td></tr>`;
    }
    return this._tickets.map(t => {
      const cls   = STATUS_CLASSES[t.status as TicketStatusValue] ?? 'badge-neutral';
      const label = STATUS_LABELS[t.status as TicketStatusValue] ?? t.status;
      const created = new Date(t.createdTS);
      const dateStr = created.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = created.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      const updated = new Date(t.updatedTS);
      const updatedStr = updated.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

      return `
        <tr>
          <td class="col-num mono">${t.ticketNum}</td>
          <td><span class="status-badge ${cls}">${label}</span></td>
          <td class="col-ts">
            <span class="ts-date">${dateStr}</span>
            <span class="ts-time mono">${timeStr}</span>
          </td>
          <td class="col-ts">
            <span class="ts-date">${updatedStr}</span>
          </td>
        </tr>`;
    }).join('');
  }

  private _render() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>Tickets</span>
          <div class="header-actions">
            <button class="btn-back" id="back-btn">← Back</button>
            <nu-user-menu></nu-user-menu>
          </div>
        </header>

        <div class="body">
          <div class="filters">
            <div class="search-group">
              <label class="filter-label mono" for="search-input">Ticket #</label>
              <div class="search-wrap">
                <input
                  class="search-input"
                  id="search-input"
                  type="text"
                  placeholder="e.g. 0482"
                  value="${this._search}"
                  autocomplete="off"
                  inputmode="numeric"
                >
                ${this._search ? `<button class="clear-btn" id="clear-btn" aria-label="Clear">✕</button>` : ''}
              </div>
            </div>

            <label class="show-all-label">
              <input type="checkbox" id="show-all" ${this._showAll ? 'checked' : ''} ${this._search ? 'disabled' : ''}>
              <span class="mono">Show all</span>
            </label>

            <button class="btn-apply" id="apply-btn" ${this._loading ? 'disabled' : ''}>
              ${this._loading ? '<span class="spinner-sm"></span>' : 'Search'}
            </button>
          </div>

          ${!this._loading && !this._error && !this._showAll && !this._search ? `
            <p class="filter-note mono">Showing active tickets from the last 30 days.</p>
          ` : ''}

          ${this._error ? `<p class="error-msg">${this._error}</p>` : ''}

          <div class="table-wrap">
            <table class="ticket-table">
              <thead>
                <tr>
                  <th class="col-num">Ticket #</th>
                  <th>Status</th>
                  <th class="col-ts">Checked In</th>
                  <th class="col-ts">Updated</th>
                </tr>
              </thead>
              <tbody>
                ${this._loading
                  ? `<tr><td class="empty-cell" colspan="4"><span class="spinner-sm spinner-dark"></span> Loading…</td></tr>`
                  : this._rowsHTML()}
              </tbody>
            </table>
          </div>

          ${!this._loading && this._tickets.length === 200 ? `
            <p class="limit-note mono">Showing first 200 results — search by ticket number to narrow results.</p>
          ` : ''}
        </div>
      </div>
    `;

    this.root.querySelector('#back-btn')!.addEventListener('click', () => navigate(`/app/org/${this.orgId}`));

    this.root.querySelector('#apply-btn')!.addEventListener('click', () => {
      this._search   = (this.root.querySelector<HTMLInputElement>('#search-input')!).value.trim();
      this._showAll  = (this.root.querySelector<HTMLInputElement>('#show-all')!).checked;
      this._load();
    });

    this.root.querySelector<HTMLInputElement>('#search-input')!.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') {
        this._search  = (this.root.querySelector<HTMLInputElement>('#search-input')!).value.trim();
        this._showAll = (this.root.querySelector<HTMLInputElement>('#show-all')!).checked;
        this._load();
      }
    });

    const clearBtn = this.root.querySelector('#clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this._search = '';
        this._load();
      });
    }

    this.root.querySelector<HTMLInputElement>('#show-all')!.addEventListener('change', (e) => {
      this._showAll = (e.target as HTMLInputElement).checked;
      this._load();
    });
  }

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100vh;
        background: var(--paper-light, #FAF7F0);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }

      .top-bar {
        display: flex; align-items: center; justify-content: space-between;
        padding: 12px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(250,247,240,0.95);
        backdrop-filter: blur(6px);
        position: sticky; top: 0; z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 18px; letter-spacing: -0.02em;
        display: flex; align-items: center; gap: 7px;
      }
      .dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block; flex-shrink: 0;
      }
      .header-actions { display: flex; align-items: center; gap: 10px; }
      .btn-back {
        background: transparent; border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px; padding: 5px 12px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink-soft, #5A5346); cursor: pointer; transition: background .15s;
      }
      .btn-back:hover { background: var(--paper-light, #FAF7F0); }

      .body {
        max-width: 860px; margin: 0 auto;
        padding: 24px 20px 48px;
        display: flex; flex-direction: column; gap: 16px;
      }

      /* filters */
      .filters {
        display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
        padding: 16px;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
      }
      .search-group { display: flex; flex-direction: column; gap: 5px; }
      .filter-label {
        font-size: 10px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-soft, #5A5346);
      }
      .search-wrap { position: relative; display: flex; align-items: center; }
      .search-input {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 4px; padding: 6px 10px;
        font-family: 'IBM Plex Mono', monospace; font-size: 13px;
        color: var(--ink, #2A2622); width: 140px;
      }
      .search-input:focus {
        outline: 2px solid var(--kiln, #C97A2E); outline-offset: 1px; border-color: transparent;
      }
      .clear-btn {
        position: absolute; right: 6px;
        background: none; border: none; padding: 0;
        font-size: 11px; color: var(--ink-soft, #5A5346);
        cursor: pointer; line-height: 1;
      }
      .clear-btn:hover { color: var(--ink, #2A2622); }

      .show-all-label {
        display: flex; align-items: center; gap: 6px;
        font-size: 13px; color: var(--ink-soft, #5A5346);
        cursor: pointer; padding-bottom: 1px;
      }
      .show-all-label input[type="checkbox"] { accent-color: var(--kiln, #C97A2E); cursor: pointer; }
      .show-all-label input:disabled { cursor: not-allowed; opacity: 0.5; }

      .btn-apply {
        display: flex; align-items: center; gap: 6px;
        background: var(--kiln, #C97A2E); color: var(--paper-light, #FAF7F0);
        border: none; border-radius: 4px; padding: 7px 18px;
        font-family: 'Inter', sans-serif; font-weight: 600; font-size: 13px;
        cursor: pointer; transition: background .15s; align-self: flex-end;
      }
      .btn-apply:hover:not(:disabled) { background: var(--kiln-dark, #A15F1F); }
      .btn-apply:disabled { opacity: 0.6; cursor: not-allowed; }

      .filter-note {
        font-size: 11px; color: var(--ink-soft, #5A5346); margin: 0;
      }
      .error-msg { font-size: 13px; color: #c0392b; font-family: 'IBM Plex Mono', monospace; margin: 0; }
      .limit-note { font-size: 11px; color: var(--ink-soft, #5A5346); margin: 0; text-align: center; }

      /* table */
      .table-wrap { border: 1px solid var(--line, #C9C0AC); border-radius: 6px; overflow: hidden; }
      .ticket-table { width: 100%; border-collapse: collapse; font-size: 13px; }
      .ticket-table thead tr {
        background: var(--paper, #E4DED2);
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .ticket-table th {
        padding: 10px 14px; text-align: left;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 10px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-soft, #5A5346); font-weight: 600;
      }
      .ticket-table tbody tr {
        background: var(--paper-light, #FAF7F0);
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .ticket-table tbody tr:last-child { border-bottom: none; }
      .ticket-table td { padding: 10px 14px; vertical-align: middle; }

      .col-num { width: 100px; }
      .col-ts { width: 160px; }
      .ts-date { display: block; font-size: 13px; color: var(--ink, #2A2622); }
      .ts-time { display: block; font-size: 11px; color: var(--ink-soft, #5A5346); }

      .status-badge {
        display: inline-block; padding: 2px 9px;
        border-radius: 10px; font-size: 12px; font-weight: 600; white-space: nowrap;
      }
      .badge-in   { background: rgba(60,110,170,.12);  color: #2a5fa5; }
      .badge-out  { background: rgba(201,122,46,.12);  color: var(--kiln-dark, #A15F1F); }
      .badge-done { background: rgba(110,127,92,.15);  color: var(--glaze, #6E7F5C); }
      .badge-err  { background: rgba(192,57,43,.12);   color: #c0392b; }

      .empty-cell {
        text-align: center; padding: 40px 14px;
        color: var(--ink-soft, #5A5346);
        font-family: 'IBM Plex Mono', monospace; font-size: 13px;
      }

      .spinner-sm {
        display: inline-block; width: 14px; height: 14px;
        border: 2px solid rgba(250,247,240,.4);
        border-top-color: var(--paper-light, #FAF7F0);
        border-radius: 50%; animation: spin .7s linear infinite; vertical-align: middle;
      }
      .spinner-dark {
        border-color: var(--line, #C9C0AC);
        border-top-color: var(--kiln, #C97A2E);
      }
      @keyframes spin { to { transform: rotate(360deg); } }
    </style>`;
  }
}

customElements.define('nu-tickets', NuTickets);